import React from 'react';
import { Button } from 'primereact/button';
import { classNames } from 'primereact/utils';
import { LOCALES } from '../../../config/locales';
import { ValidationErrors } from '../../../hooks/useForm';
import { RoutineDurationExercise, RoutineExercise, RoutineRepetitionExercise } from '../../../types/Fisiofit/routine';
import { ExerciseShort } from '../../../types/Fisiofit/exercise';
import { MUSCLE_GROUPS, EXERCISE_CATEGORIES } from '../../../types/Fisiofit/fisiofitConst';
import { ExerciseViewIcon } from '../../ExerciseViewIcon/ExerciseViewIcon';
import { RoutineForm } from '../validators';
import { RoutineSupersetsActions } from '../types';
import { ExerciseDetails } from './ExerciseDetails';
import { SupersetExerciseFields } from './SupersetExerciseFields';

interface SupersetItemProps {
  exercise: RoutineExercise[];
  dayIndex: number;
  exerciseIndex: number;
  exerciseId: string;
  isExpanded: boolean;
  isDragging: boolean | undefined;
  provided?: any;
  isEditing: boolean;
  isCreating: boolean;
  validationErrors: ValidationErrors<RoutineForm> | undefined;
  locale: LOCALES;
  organizationExercises: ExerciseShort[] | undefined;
  selectedExerciseMuscleGroups: MUSCLE_GROUPS[];
  selectedExerciseCategories: EXERCISE_CATEGORIES[];
  expandedExerciseIds: Record<string, boolean>;
  setExpandedExerciseIds: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  supersets: RoutineSupersetsActions;
}

export const SupersetItem = ({
  exercise,
  dayIndex,
  exerciseIndex,
  exerciseId,
  isExpanded,
  isDragging,
  provided,
  isEditing,
  isCreating,
  validationErrors,
  locale,
  organizationExercises,
  selectedExerciseMuscleGroups,
  selectedExerciseCategories,
  expandedExerciseIds,
  setExpandedExerciseIds,
  supersets,
}: SupersetItemProps) => {
  return (
    <div
      className={classNames('exercise-item', 'superset-item', {
        expanded: isExpanded,
        'is-dragging': isDragging,
        'invalid-exercise': validationErrors?.exerciseDays && exercise.some((ex) => !ex.exerciseId),
      })}
      ref={provided?.innerRef}
      {...provided?.draggableProps}
      style={{
        ...provided?.draggableProps?.style,
        ...(isDragging && { zIndex: 9999 }),
      }}
      data-exercise-id={exerciseId}
      data-is-expanded={isExpanded ? 'true' : 'false'}
    >
      <div className={`superset-content ${!isEditing && !isCreating ? 'viewing-mode' : ''}`}>
        {/* Drag handle integrado en el layout */}
        {(isEditing || isCreating) && provided && (
          <div className='superset-drag-handle' {...provided.dragHandleProps}>
            <i className='pi pi-bars'></i>
          </div>
        )}

        <div className='superset-exercises-container'>
          {/* Renderizar cada ejercicio de la superserie como una fila separada */}
          {exercise.map((supersetExercise, supersetExIndex) => {
            const supersetExerciseId = `${dayIndex}-${exerciseIndex}-${supersetExIndex}`;
            const isThisExerciseExpanded = expandedExerciseIds[supersetExerciseId] || false;

            return (
              <div
                key={supersetExIndex}
                className={classNames(`superset-exercise-row ${supersetExIndex === 0 ? 'first-exercise' : 'second-exercise'}`, {
                  expanded: isThisExerciseExpanded,
                })}
              >
                <div className='exercise-header'>
                  {supersetExercise.comment && (
                    <div className='comment-indicator'>
                      <i className='pi pi-comment'></i>
                    </div>
                  )}
                  <ExerciseViewIcon exercise={supersetExercise} />
                  <div className='exercise-info'>
                    <span className='exercise-name'>{supersetExercise.name || 'Ejercicio sin nombre'}</span>
                  </div>
                  {isEditing || isCreating ? (
                    <div className='exercise-actions'>
                      <Button
                        icon={isThisExerciseExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                        text
                        rounded
                        className='expand-button'
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          setExpandedExerciseIds((prev) => {
                            const newState = { ...prev };
                            if (isThisExerciseExpanded) {
                              delete newState[supersetExerciseId];
                            } else {
                              newState[supersetExerciseId] = true;
                            }
                            return newState;
                          });
                        }}
                      />
                      <Button
                        icon='pi pi-trash'
                        text
                        rounded
                        className='p-button-danger'
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          supersets.removeSupersetExercise(dayIndex, exerciseIndex, supersetExIndex);
                        }}
                        title='Eliminar ejercicio de la superserie'
                      />
                    </div>
                  ) : (
                    <div className='exercise-actions'>
                      <Button
                        icon={isThisExerciseExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                        text
                        rounded
                        className='expand-button'
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          setExpandedExerciseIds((prev) => {
                            const newState = { ...prev };
                            if (isThisExerciseExpanded) {
                              delete newState[supersetExerciseId];
                            } else {
                              newState[supersetExerciseId] = true;
                            }
                            return newState;
                          });
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Panel de edición individual para cada ejercicio */}
                {isThisExerciseExpanded && (
                  <div className='exercise-edit-panel'>
                    {!isEditing && !isCreating && (
                      <ExerciseDetails exercise={supersetExercise as RoutineRepetitionExercise | RoutineDurationExercise} />
                    )}

                    {(isEditing || isCreating) && (
                      <SupersetExerciseFields
                        supersetExercise={supersetExercise}
                        dayIndex={dayIndex}
                        exerciseIndex={exerciseIndex}
                        supersetExIndex={supersetExIndex}
                        validationErrors={validationErrors}
                        locale={locale}
                        organizationExercises={organizationExercises}
                        selectedExerciseMuscleGroups={selectedExerciseMuscleGroups}
                        selectedExerciseCategories={selectedExerciseCategories}
                        supersets={supersets}
                      />
                    )}
                  </div>
                )}

                {/* Mostrar el botón de desvincular solo entre el primer y segundo ejercicio */}
                {supersetExIndex === 0 && (isEditing || isCreating) && (
                  <Button
                    icon='pi pi-times'
                    text
                    rounded
                    className='superset-break-button'
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      supersets.handleBreakSuperset(dayIndex, exerciseIndex);
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
